import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import PublicLayout from '../layouts/PublicLayout.jsx';
import AuthLayout from '../layouts/AuthLayout.jsx';
import CustomerLayout from '../layouts/CustomerLayout.jsx';
import AdminLayout from '../layouts/AdminLayout.jsx';
import MarketerLayout from '../layouts/MarketerLayout.jsx';

import ProtectedRoute from './ProtectedRoute.jsx';
import RoleBasedRoute from './RoleBasedRoute.jsx';

import publicRoutes from './publicRoutes.js';
import authRoutes from './authRoutes.js';
import customerRoutes from './customerRoutes.js';
import adminRoutes from './adminRoutes.js';
import marketerRoutes from './marketerRoutes.js';

import Loader from '../components/common/Loader.jsx';
import NotFound from '../pages/public/NotFound.jsx';
import { ROLES } from '../constants/roles.js';

// AdminLoginPage uses its own full-screen layout, so it sits outside AuthLayout.
const AdminLoginPage = lazy(() => import('../pages/auth/AdminLoginPage.jsx'));

export default function AppRoutes() {
  return (
    <Suspense fallback={<Loader fullScreen />}>
      <Routes>
        {/* Staff login - standalone (no public chrome) */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* Public */}
        <Route element={<PublicLayout />}>
          {publicRoutes.map(({ path, Component, requireAuth }) => (
            <Route
              key={path}
              path={path}
              element={
                requireAuth ? (
                  <ProtectedRoute>
                    <Component />
                  </ProtectedRoute>
                ) : (
                  <Component />
                )
              }
            />
          ))}
        </Route>

        {/* Auth (customer flows) */}
        <Route element={<AuthLayout />}>
          {authRoutes.map(({ path, Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
        </Route>

        {/* Customer area - customer role only */}
        <Route
          path="/account"
          element={
            <RoleBasedRoute allowed={[ROLES.CUSTOMER]}>
              <CustomerLayout />
            </RoleBasedRoute>
          }
        >
          {customerRoutes.map(({ path, Component }) => (
            <Route key={path || 'index'} path={path} element={<Component />} />
          ))}
        </Route>

        {/* Admin area */}
        <Route
          path="/admin"
          element={
            <RoleBasedRoute allowed={[ROLES.ADMIN]}>
              <AdminLayout />
            </RoleBasedRoute>
          }
        >
          {adminRoutes.map(({ path, Component }) => (
            <Route key={path || 'index'} path={path} element={<Component />} />
          ))}
        </Route>

        {/* Marketer area - admin can also access */}
        <Route
          path="/marketer"
          element={
            <RoleBasedRoute allowed={[ROLES.MARKETER, ROLES.ADMIN]}>
              <MarketerLayout />
            </RoleBasedRoute>
          }
        >
          {marketerRoutes.map(({ path, Component }) => (
            <Route key={path || 'index'} path={path} element={<Component />} />
          ))}
        </Route>

        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
  );
}

// Kept for callers that previously imported ProtectedRoute via this file.
export { ProtectedRoute };
